export const COMPANY = {
  name: "West Coral Communications",
  legal: "West Coral Communications LLC",
  tagline: "Building the Future of Connectivity",
  address: "74 Red Fern Rd, Campbellsville, KY 42718",
  email: "accounting@westcoralcommunications.com",
  recruitingContact: { name: "Chad", email: "Chad@WestCoralCommunications.com" },
  regions: ["Kentucky", "Ohio", "Florida", "Southeast U.S.", "Nationwide Mobilization"],
};

export const CORE_VALUES = [
  { n: "01", t: "Safety", d: "Every job starts and ends with the safe return of our crews." },
  { n: "02", t: "Integrity", d: "We do what we say — on the print, in the ground, on the invoice." },
  { n: "03", t: "Quality", d: "Workmanship that passes inspection the first time, every time." },
  { n: "04", t: "Accountability", d: "We own our production, our documentation, and our results." },
  { n: "05", t: "Customer Focus", d: "We make our primes and partners look good to their customers." },
];

export const SERVICE_GROUPS = [
  {
    title: "Core Services",
    items: [
      "Turnkey Underground Fiber Construction",
      "Fiber Placement",
      "Directional Boring & Missile Boring",
      "Plowing & Trenching",
      "Restoration",
      "Emergency Response",
    ],
  },
  {
    title: "Program Delivery",
    items: [
      "Project Management",
      "Nationwide Mobilization",
      "Experienced Self-Performing Crews",
      "Estimating & Pre-Construction",
      "Quality Control & As-Builts",
      "Closeout Documentation",
    ],
  },
  {
    title: "Utility & Network",
    items: [
      "FTTH Construction",
      "Conduit & Microduct Installation",
      "Handholes, Vaults & Pedestals",
      "Road, Rail & Utility Crossings",
      "Network Expansion",
      "Utility Relocations",
    ],
  },
];

export const FLEET = [
  "Ditch Witch RT125 Quad Track Plow",
  "Ditch Witch RT120",
  "Mini Excavators",
  "Missile Boring Equipment",
  "Vacuum Excavation",
  "Reel Trailers",
  "Equipment Trailers",
  "Service Trucks",
  "Utility Trucks",
];

export const CAPABILITIES = [
  "Nationwide Mobilization",
  "Turnkey Program Delivery",
  "Long-Haul Fiber",
  "FTTH",
  "Rural Broadband",
  "Urban Construction",
  "Greenfield & Brownfield",
  "Emergency Response",
  "Utility Relocations",
];

export const WHY = [
  "Nationwide mobilization capacity",
  "Experienced self-performing crews",
  "Turnkey project delivery — one accountable partner",
  "Modern, self-owned equipment fleet",
  "Safety-driven culture",
  "Reliable scheduling and daily field reporting",
  "Emergency response capability, 24/7",
  "Executive-level project management",
];

export const TRUST_BADGES = [
  "Fully Insured",
  "OSHA Compliant",
  "Drug-Free Workplace",
  "Safety First",
  "Prime Contractor Ready",
  "Emergency Mobilization",
  "Multi-State Operations",
  "Fiber Optic Specialists",
];

export const COVERAGE = [
  "General Liability Insurance",
  "Commercial Auto Liability",
  "Workers’ Compensation",
  "Umbrella / Excess Liability",
  "Equipment Coverage",
  "Additional Insured Endorsements",
  "Waiver of Subrogation (when required)",
  "COIs issued within 24 hours",
];

export const COMPLIANCE = [
  "OSHA safety compliance",
  "Drug-free workplace program",
  "Driver qualification & MVR reviews",
  "Daily equipment inspections",
  "Site-specific safety plans",
  "Utility damage prevention & 811 compliance",
  "Job hazard analysis (JHA)",
  "Tailgate safety meetings",
];

export const PARTNERS = [
  "MasTec",
  "Windstream",
  "Brightspeed",
  "Spectrum",
  "Kanaan",
  "Dycom",
  "Congruex",
  "Municipalities",
];

// States where WCC has active or completed projects / crews.
// "home" = home base (bright red), "active" = recent/current work (red), "available" = nationwide reach (muted).
export const HOME_STATES = ["KY"];
export const ACTIVE_STATES = ["OH", "FL", "IN", "TN", "GA", "AL", "SC", "NC", "VA", "WV", "PA"];

export const JOBS = [
  {
    id: "foreman",
    title: "Underground Fiber Construction Foreman",
    blurb:
      "Lead a field crew, coordinate daily production, maintain jobsite safety, and ensure work meets project specifications.",
    responsibilities: [
      "Lead and supervise underground fiber construction crews",
      "Review project plans, prints, routes, and daily assignments",
      "Coordinate equipment, materials, vehicles, and personnel",
      "Conduct daily safety meetings and jobsite inspections",
      "Ensure conduit and fiber are installed at required depth",
      "Coordinate with inspectors, locators, customers, and PM",
      "Track daily footage, labor hours, materials, and production",
      "Complete redlines, field reports, photos, and as-builts",
    ],
    qualifications: [
      "Previous underground telecom construction experience",
      "Experience supervising plow, trench, missile, or boring crews",
      "Ability to read construction prints and utility maps",
      "Valid driver’s license with acceptable record (CDL preferred)",
      "Strong leadership and communication skills",
    ],
  },
  {
    id: "operator",
    title: "Cable Plow Operator",
    blurb:
      "Safely operate equipment used to install telecommunications conduit and fiber facilities.",
    responsibilities: [
      "Operate cable plows, trenchers, skid steers, and excavators",
      "Complete daily equipment inspections",
      "Follow marked routes, prints, and project specifications",
      "Maintain required installation depth",
      "Work safely around existing utilities and active roadways",
      "Assist with equipment maintenance and minor repairs",
    ],
    qualifications: [
      "Experience operating Ditch Witch RT120, RT125, or similar",
      "Underground utility or fiber-optic construction experience",
      "CDL and equipment-hauling experience strongly preferred",
      "Ability to work outdoors in varying weather",
    ],
  },
  {
    id: "missile",
    title: "Missile & Pneumatic Boring Crew",
    blurb:
      "Install conduit beneath roads, sidewalks, and driveways using pneumatic boring equipment.",
    responsibilities: [
      "Set up and operate pneumatic boring equipment",
      "Excavate launch and receiving pits",
      "Track bore alignment and depth",
      "Connect air compressors, hoses, and boring tools",
      "Install conduit following completion of the bore",
      "Safely expose and protect existing utilities",
    ],
    qualifications: [
      "Missile boring or underground construction experience",
      "Knowledge of air compressors and pneumatic boring tools",
      "Ability to work safely around underground utilities",
      "Valid driver’s license (CDL preferred)",
    ],
  },
  {
    id: "trench",
    title: "Trench Crew Operator & Laborer",
    blurb:
      "Install telecommunications conduit using trenchers, excavators, skid steers, and hand tools.",
    responsibilities: [
      "Operate trenching and excavation equipment when qualified",
      "Assist with conduit placement and trench preparation",
      "Maintain proper depth and separation requirements",
      "Hand-dig around marked utilities",
      "Backfill, compact, and restore disturbed areas",
    ],
    qualifications: [
      "Underground utility construction experience preferred",
      "Equipment operating experience is a plus",
      "Ability to perform physical work outdoors",
      "Valid driver’s license and reliable transportation",
    ],
  },
  {
    id: "laborer",
    title: "Underground Construction Laborer",
    blurb:
      "An excellent opportunity to begin or advance a career in telecommunications construction.",
    responsibilities: [
      "Assist operators and crew leaders with daily activities",
      "Hand-dig and expose utilities",
      "Place conduit, pull rope, tracer wire, vaults, and handholes",
      "Set up work zones and traffic-control devices",
      "Assist with cleanup, backfilling, compaction, and restoration",
    ],
    qualifications: [
      "Construction experience helpful but not always required",
      "Willingness to learn underground fiber construction",
      "Ability to work outdoors and perform physical labor",
      "Valid driver’s license preferred",
    ],
  },
  {
    id: "cdl",
    title: "CDL Driver & Equipment Transporter",
    blurb:
      "Safely transport construction equipment, materials, and trailers between yards and project locations.",
    responsibilities: [
      "Transport construction equipment and materials",
      "Perform pre-trip and post-trip inspections",
      "Properly load and secure equipment",
      "Maintain driver logs and required documentation",
      "Follow DOT, FMCSA, and company safety requirements",
    ],
    qualifications: [
      "Valid Class A CDL",
      "Acceptable motor vehicle record",
      "Experience hauling heavy equipment and trailers",
      "Current DOT medical certification",
    ],
  },
  {
    id: "restoration",
    title: "Restoration Crew Members",
    blurb:
      "Return disturbed work areas to a professional and acceptable condition after construction.",
    responsibilities: [
      "Repair disturbed lawns and landscaping",
      "Install seed, straw, sod, and topsoil",
      "Repair gravel areas and driveways",
      "Assist with concrete and asphalt restoration",
      "Document completed restoration work",
    ],
    qualifications: [
      "Landscaping, concrete, asphalt, or construction experience",
      "Experience operating compact equipment is helpful",
      "Valid driver’s license",
      "Strong attention to detail",
    ],
  },
];

export const HIRING_STEPS = [
  { n: "01", t: "Submit Your Application", d: "Provide your employment history, qualifications, licenses, and contact information." },
  { n: "02", t: "Initial Review", d: "Our management team reviews your experience against available positions." },
  { n: "03", t: "Phone Interview", d: "A short call to discuss experience, travel availability, and openings." },
  { n: "04", t: "In-Person / Video Interview", d: "Meet a manager to discuss the role and employment terms." },
  { n: "05", t: "Employment Screening", d: "MVR review, background, drug screening, and certification verification as required." },
  { n: "06", t: "Offer & Onboarding", d: "Complete payroll, safety, and policy onboarding documents." },
  { n: "07", t: "Orientation & Field Assignment", d: "Complete required orientation before your crew or project assignment." },
];

export const RESOURCES = [
  { name: "W-9", desc: "Request Form W-9 for vendor setup." },
  { name: "Certificate of Insurance (COI)", desc: "COIs issued within 24 hours of request." },
  { name: "Capability Statement", desc: "Executive one-pager for procurement teams." },
  { name: "Equipment List", desc: "Current fleet and boring capabilities." },
  { name: "Service Area Map", desc: "KY, OH, FL and surrounding markets." },
  { name: "Safety Manual Summary", desc: "OSHA-aligned safety program overview." },
  { name: "EMR Letter", desc: "Experience Modification Rate (on request)." },
  { name: "NAICS / UEI / CAGE", desc: "Registration identifiers (if applicable)." },
];
